import { AfterViewInit, Component, computed, input, signal } from '@angular/core';
import { NotificationLayoutComponent } from '../../notification-detail-layout/notification-detail-layout.component';
import {
  NotificationPreamble,
  NotificationStep,
  TeacherNotification,
  TeacherUsageNotification
} from '../../../core/models/notification.model';
import {
  SUBSCRIPTION_TYPE_INTERVENTION,
  SUBSCRIPTION_TYPE_DIAGNOSTIC,
  SUBSCRIPTION_TYPE_SCREENER,
  SUBSCRIPTION_TYPE_DIAGNOSTIC_MID,
  SUBSCRIPTION_TYPE_DIAGNOSTIC_POST
} from '../../../core/constants/subscription';

@Component({
  selector: 'wf-completed-assessment-notification',
  imports: [
    NotificationLayoutComponent
  ],
  template: `
    <wf-notification-layout
      [headerTitle]="title()"
      [preambles]="preambles()"
      [steps]="steps()"
      [ids]="ids()"
      [ctaLabel]="ctaLabel()"
      [ctaRoute]="ctaRoute()"
      [closingText]="closingText()"
    >
      <table table class="table-wrap">
        <tr>
          <th class="th">Student</th>
          <th class="th">Date</th>
          <th class="th">Assessment</th>
        </tr>
        @for (notification of data(); track notification.alertID) {
          <tr>
            <td class="td">{{ notification.studentName }}</td>
            <td class="td" style="white-space:nowrap; color:#a2a2a2;">{{ notification.data['displaydate'] }}</td>
            <td class="td"><span>{{ getAssessment(notification.data.SubscriptionType, notification.data.AssessmentType) }}</span></td>
          </tr>
        }
      </table>
    </wf-notification-layout>
  `,
  standalone: true
})
export class CompletedAssessmentNotificationComponent implements AfterViewInit {
  preambles = signal<NotificationPreamble[]>([]);
  steps = signal<NotificationStep[]>([]);
  title = signal<string>('');
  ctaLabel = signal<string>('');
  ctaRoute = signal<string>('');
  closingText = signal<string>('');
  readonly data = input.required<TeacherNotification[]>();
  readonly ids = computed(() => {
    const notifications = this.data() ?? [];

    return { alerts: notifications.map(notification => notification.alertID) };
  });

  ngAfterViewInit() {
    if (this.data()) {
      const assessment: number = parseInt(this.data()[0]?.data.AssessmentType);
      if (!isNaN(assessment) && assessment === SUBSCRIPTION_TYPE_SCREENER) {
        this.title.set('They did it! Your student(s) completed a Screener 👏');
        this.ctaLabel.set('Go to Student Subscriptions →');
        this.ctaRoute.set('/students/manage-subscriptions');
        this.preambles.set([{
          text: 'The student(s) below completed the WordFlight Screener today. The Screener reliably predicts '
            + 'performance on standardized reading measures — it tells you whether a student’s foundational skills are '
            + 'likely proficient, at some risk, or at high risk. Here’s what to do with the results.'
        }]);
        this.steps.set([
          {
            title: 'Go to Enroll > Student Subscriptions',
            body: 'Use the checkbox next to each student’s name to enroll.'
          },
          {
            title: 'Use these guidelines to decide who to enroll',
            body: '<p><span style="color: #B91C1C; font-weight: bold;">Red (high risk)</span> or <span style="color: #F2AE43; font-weight: bold;">yellow (some risk)</span>: Strong candidate for WordFlight. </p>'
              + '<p><span style="color: #555555; font-weight: bold;">Score below 50 and/or no sound-letter correspondence</span>: Not yet ready for WordFlight.</p>'
              + '<p><span style="color: #0F54A6; font-weight: bold;">Borderline proficient (400–450 for grades 2–4, or 300–350 for grade 5+)</span>: May benefit — cross-reference your benchmark data to decide.</p>'
          }
        ]);
        this.closingText.set('Not sure where a student lands? Reach out — we’re happy to help you read the results.');
      } else {
        this.title.set('They did it! Your student(s) completed a Diagnostic 📊');
        this.ctaLabel.set('Open Diagnostic Results →');
        this.ctaRoute.set('/reports/system/diagnostic');
        this.preambles.set([{
          text: 'The student(s) below completed the WordFlight Diagnostic today. '
            + 'This is our robust, patented assessment of foundational skills — validated against the '
            + 'Woodcock-Johnson™ Assessment — and it gives you a detailed picture of where each student stands.'
        }]);
        this.steps.set([
          {
            title: 'Review the Diagnostic Results report',
            body: 'Head to the WordFlight Management Site, select <b>Diagnostic Results</b>, then click <b>View</b> next to the student to see their full report.'
          },
          {
            title: 'Share the results with your student',
            body: 'A brief review together is a great opportunity to celebrate their strengths and reinforce why daily WordFlight practice matters. This can also help students stay motivated.'
          },
          {
            title: 'Use the data to inform your instruction',
            body: 'Diagnostic results are great for informing small group assignments and monitoring growth over time. Reach out to your Partner Success Lead if you’d like help making the most of the data.',
            variant: 'green'
          },
        ])
      }
    }
  }

  getAssessment(subscription: string, assessment: string): string {
    const subscriptionValue = parseInt(subscription, 10);
    const assessmentValue = parseInt(assessment, 10);

    if (isNaN(subscriptionValue) || isNaN(assessmentValue)) return '';

    if (subscriptionValue === SUBSCRIPTION_TYPE_INTERVENTION) {
      if (assessmentValue === SUBSCRIPTION_TYPE_DIAGNOSTIC) {
        return 'System Pre';
      } else if (assessmentValue == SUBSCRIPTION_TYPE_DIAGNOSTIC_MID) {
        return 'System Mid';
      } else if (assessmentValue === SUBSCRIPTION_TYPE_DIAGNOSTIC_POST) {
        return ' System Post';
      }
    } else if (subscriptionValue === SUBSCRIPTION_TYPE_DIAGNOSTIC) {
      return 'Diagnostic';
    } else if (subscriptionValue === SUBSCRIPTION_TYPE_SCREENER) {
      return 'Screener';
    }

    return '';
  }
}
